import type { ConnectionHealth } from './types';

/**
 * Connection health chip. Two independent signals fold into one badge:
 * whether the OAuth credential still works, and how the most recent sync went.
 * A credential problem always wins, because that is the one needing a human to
 * go and re-authorise.
 */
export function StatusBadge({
  status,
  lastRunStatus,
}: {
  status: string | null;
  lastRunStatus?: string | null;
}) {
  if (status === 'expired') return <span className="badge err">reconnect</span>;
  if (status === 'revoked') return <span className="badge err">revoked</span>;
  if (status === 'error') return <span className="badge err">error</span>;
  if (lastRunStatus === 'failed') return <span className="badge err">sync failed</span>;
  if (lastRunStatus === 'partial') return <span className="badge warn">partial</span>;
  if (lastRunStatus === 'running') return <span className="badge warn">syncing</span>;
  if (status === 'active') return <span className="badge ok">active</span>;
  return <span className="badge idle">{status ?? 'idle'}</span>;
}

export function relativeTime(iso: string | null): string {
  if (!iso) return 'never';
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 60) return 'just now';

  const scale: Array<[number, string]> = [
    [60, 'minute'],
    [3600, 'hour'],
    [86_400, 'day'],
    [604_800, 'week'],
    [2_592_000, 'month'],
  ];

  let [size, unit] = scale[0]!;
  for (const [s, u] of scale) {
    if (seconds >= s) [size, unit] = [s, u];
  }

  const count = Math.floor(seconds / size);
  return `${count} ${unit}${count === 1 ? '' : 's'} ago`;
}

export function Notice({
  kind,
  children,
}: {
  kind: 'ok' | 'err' | 'warn';
  children: React.ReactNode;
}) {
  return <div className={`notice ${kind}`}>{children}</div>;
}

export function latestRun(rows: ConnectionHealth[]): string | null {
  const times = rows.map((r) => r.last_run_at).filter((t): t is string => Boolean(t));
  if (times.length === 0) return null;
  return times.sort((a, b) => new Date(b).getTime() - new Date(a).getTime())[0]!;
}
